// ./src/pages/private/EventTicketsPage/priceTiers.ts
//
// ROADMAP #162 — a ticket type's price ladder ("the first 40 at $10, the next 40 at $15, then the regular price") as the
// host edits it. The server writes the ladder as availability rules (`ticketPriceTiersSet`, `tiersToRules` in the API);
// this reads them back as rows. Pure, so check-price-tiers (apps/app) runs it.
import type { TicketPriceRule } from '@/utils/api/TicketPriceRuleService';

/** Mirrors `TICKET_PRICE_RULE_KIND.AVAILABILITY` — kept as a value so this module stays import-free for the guard. */
export const AVAILABILITY_KIND = 'availability';

/** Mirrors the server's `PRICE_TIERS_MAX`. */
export const PRICE_TIERS_MAX = 20;

/** One row of the editor: the next `quantity` tickets at `price` (null while the host is still typing). */
export interface TierRow {
  quantity: number | null;
  price: number | null;
}

/** Is this rule part of the ladder (priced by how many have sold)? Those are edited as tiers, never one by one. */
export const isTierRule = (r: Pick<TicketPriceRule, 'ruleKind'>): boolean => r.ruleKind === AVAILABILITY_KIND;

/** The ladder's rows, in order, from a type's rules: each covers `maxSold − minSold + 1` tickets at its price. */
export const tiersOf = (rules: TicketPriceRule[]): Array<{ quantity: number; price: number; from: number; to: number }> =>
  rules
    .filter(isTierRule)
    .map((r) => ({ from: Number(r.minSold ?? 0) + 1, to: Number(r.maxSold ?? r.minSold ?? 0) + 1, price: Number(r.amount ?? 0) }))
    .sort((a, b) => a.from - b.from)
    .map((t) => ({ ...t, quantity: Math.max(1, t.to - t.from + 1) }));

/** "Tickets 1–40", "Tickets 41–80" — the ticket numbers each row covers, as the host types the rows. */
export const tierRanges = (rows: TierRow[]): Array<{ from: number; to: number } | null> => {
  let next = 1;

  return rows.map((row) => {
    const q = Math.floor(Number(row.quantity));

    if (!Number.isFinite(q) || q < 1) {
      return null;
    }

    const range = { from: next, to: next + q - 1 };
    next = range.to + 1;

    return range;
  });
};
